import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import Link from "next/link";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import snakeCase from "lodash/snakeCase";

import { usePreviousRouteContext } from "@stadium/hooks/lib/use-previous-route";

import {
  StyledLink,
  StyledButton,
  StyledLinkProps,
  StyledButtonProps,
} from "@stadium/ui/src/components/button/styled-button";

import { LOGIN_CALLBACK_ROUTE } from "@constants/routes";
import {
  LOGIN_CALLBACK_ROUTE as SERVICES_LOGIN_CALLBACK_ROUTE,
  LOGOUT_CALLBACK_ROUTE,
  PROXY_LOGOUT_CALLBACK_ROUTE,
} from "@stadium/services/auth0/constants";

import {
  useJuneSoEventTracking,
  TrackEventArgumentsTypes,
} from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { isSameDomain } from "@stadium/helpers/routes";

import { DEFAULT_SERVICES } from "@constants/june-so";

import styles from "./main.module.scss";

type AnalyticsPropsTypes = {
  services?: readonly string[];
} & TrackEventArgumentsTypes;

type EventLocationAnalyticsProps = {
  eventProperties?: Record<string, unknown>;
  target?: string;
  action?: string;
  track?: boolean;
};

export const BackIconButton = ({
  children,
  href = "",
  className = styles.backButton,
  ...props
}) => (
  <Link href={href} passHref className={className} {...props} prefetch={false}>
    <span className={styles.backIcon}></span>
    {children || "Back to kits"}
  </Link>
);

const BASE_BACK_BUTTON_SKIPPED_ROUTES = [
  LOGIN_CALLBACK_ROUTE,
  LOGOUT_CALLBACK_ROUTE,
  PROXY_LOGOUT_CALLBACK_ROUTE,
  SERVICES_LOGIN_CALLBACK_ROUTE,
];

export const defaultBackButtonAction: (
  e: Parameters<React.MouseEventHandler<HTMLAnchorElement>>[0],
) => boolean = (e) => {
  if (window.opener) {
    e.preventDefault();
    window.opener.focus();
    window.close();
    return true;
  }
  return false;
};

type BackButtonProps = StyledLinkProps & {
  baseClassName?: string;
  checkOpener?: boolean;
  skipRoutes?: string[];
  baseSkipRoutes?: string[];
  fallbackRoute?: string;
  icon?: boolean;
  appendQueryParams?: boolean;
  allowedRoutes?: string[];
};

export const BackButton = forwardRef<
  HTMLAnchorElement & { triggerBackAction: () => void },
  BackButtonProps
>(
  (
    {
      children,
      className = "",
      baseClassName = styles.backButton,
      checkOpener = true,
      onClick,
      skipRoutes,
      isLoading,
      fallbackRoute = "/",
      baseSkipRoutes = BASE_BACK_BUTTON_SKIPPED_ROUTES,
      icon = true,
      appendQueryParams = false,
      allowedRoutes,
      ...props
    },
    ref,
  ) => {
    const { history, getPreviousUrl } = usePreviousRouteContext();
    const { replace, push } = useRouter();
    const anchorRef = useRef<HTMLAnchorElement>(null);

    const href = useMemo(() => {
      const skippedRoutes = skipRoutes?.length
        ? [...skipRoutes, ...baseSkipRoutes]
        : baseSkipRoutes;
      const prevRoute = getPreviousUrl({ skippedRoutes, allowedRoutes });
      if (!prevRoute) {
        return fallbackRoute;
      }

      if (appendQueryParams) {
        return prevRoute.href;
      }
      return prevRoute.pathname;
    }, [
      skipRoutes,
      baseSkipRoutes,
      getPreviousUrl,
      appendQueryParams,
      fallbackRoute,
      allowedRoutes,
    ]);

    const defaultBackAction = useCallback(
      (e?: React.MouseEvent<HTMLAnchorElement>) => {
        if (checkOpener && history.length === 0 && defaultBackButtonAction(e)) {
          return true;
        }

        onClick?.(e);

        if (href != fallbackRoute) {
          e?.preventDefault();
          replace(href);
          return true;
        }

        return false;
      },
      [checkOpener, history.length, href, onClick, replace, fallbackRoute],
    );

    useImperativeHandle(
      ref,
      () => ({
        ...anchorRef.current,
        triggerBackAction: () => {
          if (!defaultBackAction()) {
            push(href);
          }
        },
        defaultBackAction,
      }),
      [defaultBackAction, href, push],
    );

    return (
      <StyledLink
        href={href}
        className={`${className} ${baseClassName}`}
        onClick={defaultBackAction}
        ref={anchorRef}
        styles={styles}
        disabled={isLoading}
        {...props}
      >
        {icon && <span className={styles.backIcon}></span>}
        {children ?? "BACK TO MENU"}
      </StyledLink>
    );
  },
);

export const ExternalButton = ({
  analytics,
  eventLocationAnalytics,
  onClick,
  href,
  children,
  target,
  showExternalLinkIcon,
  ...buttonProps
}: {
  analytics?: AnalyticsPropsTypes;
  eventLocationAnalytics?: EventLocationAnalyticsProps;
  showExternalLinkIcon?: boolean;
} & StyledLinkProps) => {
  const { services = DEFAULT_SERVICES, ...analyticsProps } = analytics || {};
  const linkTarget = (() => {
    if (typeof target !== "undefined") {
      return target;
    }
    return isSameDomain({ url: href }) ? "" : "_blank";
  })();

  // TODO: Remove useJuneSoEventTracking and the trackEvent call in handleClick when June SO is removed.
  const { trackEvent } = useJuneSoEventTracking({ services });
  const withAnalytics = useWithAnalytics();

  const handleClick: StyledLinkProps["onClick"] = (...args) => {
    onClick?.(...args);
    trackEvent({ ...analyticsProps });
    const fallbackTarget = snakeCase(
      (args[0] as React.MouseEvent<HTMLAnchorElement>)?.currentTarget?.textContent ?? "",
    );
    withAnalytics(undefined, {
      ...eventLocationAnalytics,
      target: eventLocationAnalytics?.target ?? fallbackTarget,
    })();
  };

  return (
    <StyledLink
      displayChildren
      styles={styles}
      disableOnLoad
      href={href}
      onClick={handleClick}
      target={linkTarget}
      {...buttonProps}
    >
      {children}
      {showExternalLinkIcon && linkTarget === "_blank" && <span className={styles.arrowIcon} />}
    </StyledLink>
  );
};

export const InternalButton = forwardRef<
  HTMLButtonElement,
  {
    analytics?: AnalyticsPropsTypes;
    eventLocationAnalytics?: EventLocationAnalyticsProps;
  } & StyledButtonProps
>(({ analytics, eventLocationAnalytics, onClick, ...buttonProps }, ref) => {
  const { services = DEFAULT_SERVICES, ...analyticsProps } = analytics || {};

  // TODO: Remove useJuneSoEventTracking and the trackEvent call in handleClick when June SO is removed.
  const { trackEvent } = useJuneSoEventTracking({ services });
  const withAnalytics = useWithAnalytics();

  const handleClick: StyledButtonProps["onClick"] = (...args) => {
    onClick?.(...args);
    trackEvent({ ...analyticsProps });
    const fallbackTarget = snakeCase(args[0]?.currentTarget?.textContent ?? "");
    withAnalytics(undefined, {
      ...eventLocationAnalytics,
      target: eventLocationAnalytics?.target ?? fallbackTarget,
    })();
  };
  return (
    <StyledButton
      ref={ref}
      displayChildren
      styles={styles}
      disableOnLoad
      {...buttonProps}
      onClick={handleClick}
    />
  );
});
